import React from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Printer, ArrowLeft, Microscope, Camera, Eye, Video, Phone, MessageCircle, MapPin, AlertCircle, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { CLINIC } from "@/lib/clinicInfo";
import { HOME_VISIT_WA } from "@/pages/HomeVisit";

const TESTS = [
  { icon: Microscope, t: "Portable Slit Lamp", d: "Cataract, infection, dry eye & eyelid check" },
  { icon: Camera, t: "Undilated Fundus Photo", d: "Retina & optic nerve screening — no drops" },
  { icon: Eye, t: "Vision & Refraction", d: "Eyesight test & glasses power check" },
  { icon: Video, t: "Virtual Doctor Consult", d: "Findings reviewed by Dr. Gurkiran Kaur" },
];

const FOR_WHOM = ["Elderly & bedridden patients", "Recovering after surgery", "People with disabilities", "Busy professionals"];

const CSS = `
  @media print {
    .no-print { display: none !important; }
    body { background: white !important; }
    .flyer-page { box-shadow: none !important; margin: 0 !important; }
  }
  @page { size: A4; margin: 0; }
  .flyer-page { width: 210mm; height: 297mm; overflow: hidden; margin: 0 auto; background: #fbf9f4;
    box-shadow: 0 8px 30px rgba(0,0,0,0.08); display: flex; flex-direction: column;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

function QrBox({ value, label, testId }) {
  return (
    <div className="flex flex-col items-center text-center" data-testid={testId}>
      <div className="bg-white p-2.5 rounded-xl"><QRCode value={value} size={118} fgColor="#0f3d3e" /></div>
      <div className="mt-2 text-[11px] uppercase tracking-widest font-medium">{label}</div>
    </div>
  );
}

function Toolbar() {
  return (
    <div className="no-print bg-secondary/50 border-b border-border">
      <div className="container-wide py-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/eye-checkup-at-home" className="inline-flex items-center gap-2 text-sm text-primary hover:opacity-80" data-testid="flyer-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to Home Visit page
        </Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm hover:bg-primary/90" data-testid="flyer-print-btn">
          <Printer className="h-4 w-4" /> Download / Print Flyer (A4)
        </button>
      </div>
    </div>
  );
}

export default function HomeVisitFlyer() {
  const pageUrl = `${window.location.origin}/eye-checkup-at-home?utm_source=flyer&utm_medium=print&utm_campaign=home_visit`;
  return (
    <div className="bg-secondary/30 min-h-screen print:bg-white" data-testid="home-visit-flyer">
      <Seo title="Home Eye Check-up Flyer — Nehchal Eye Centre Noida" description="Printable flyer for home eye check-ups in Noida & Greater Noida." path="/eye-checkup-at-home/flyer" />
      <style>{CSS}</style>
      <Toolbar />
      <div className="py-10 print:py-0 overflow-x-auto">
        <article className="flyer-page text-foreground">
          <header className="bg-primary text-primary-foreground px-[16mm] pt-[12mm] pb-[10mm]">
            <div className="flex items-center justify-between">
              <img src={CLINIC.logo} alt="Nehchal Eye Centre logo" className="h-12 w-auto bg-white rounded-md p-1" />
              <div className="text-right text-[11px] uppercase tracking-[0.2em] opacity-80">Now in Noida &amp; Greater Noida</div>
            </div>
            <div className="mt-8 text-xs uppercase tracking-[0.25em] text-accent">Home Eye Check-up</div>
            <h1 className="font-serif text-[44px] leading-[1.05] mt-2">Can't come to the clinic?<br /><span className="italic">We'll come to you.</span></h1>
            <p className="mt-4 text-[15px] opacity-85 max-w-[150mm] leading-6">Specialist eye check-up in the comfort of your own home — led by Dr. Gurkiran Kaur, Fellowship-Trained Ophthalmologist.</p>
          </header>

          <section className="px-[16mm] pt-[9mm]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-3">What's included</div>
            <div className="grid grid-cols-2 gap-3">
              {TESTS.map(({ icon: Icon, t, d }) => (
                <div key={t} className="rounded-xl border border-border bg-white p-4 flex gap-3 items-start">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                  <div><div className="font-serif text-[17px]">{t}</div><div className="text-[12px] text-foreground/70 mt-0.5">{d}</div></div>
                </div>
              ))}
            </div>
          </section>

          <section className="px-[16mm] pt-[8mm]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-3">Ideal for</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {FOR_WHOM.map((f) => <div key={f} className="flex items-center gap-2 text-[14px]"><Check className="h-4 w-4 text-primary" />{f}</div>)}
            </div>
            <div className="mt-5 flex flex-wrap gap-2 text-[12px]">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 px-3 py-1 text-primary"><MapPin className="h-3.5 w-3.5" /> Noida &amp; Greater Noida</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 px-3 py-1"><AlertCircle className="h-3.5 w-3.5 text-accent" /> Not recommended for children under 15 yrs</span>
            </div>
          </section>

          <section className="mt-auto mx-[16mm] mb-[8mm] rounded-2xl bg-primary text-primary-foreground p-[7mm] flex items-center justify-between gap-6">
            <div>
              <div className="font-serif text-[26px] leading-tight">Book a home visit</div>
              <div className="mt-3 flex items-center gap-2 text-[18px] font-medium" data-testid="flyer-phone"><Phone className="h-5 w-5" /> {CLINIC.phone}</div>
              <div className="mt-1 flex items-center gap-2 text-[14px] opacity-85"><MessageCircle className="h-4 w-4" /> WhatsApp on the same number</div>
              <div className="mt-3 text-[12px] opacity-75">Call or WhatsApp for charges</div>
            </div>
            <div className="flex gap-4 shrink-0">
              <QrBox value={HOME_VISIT_WA} label="WhatsApp us" testId="flyer-qr-whatsapp" />
              <QrBox value={pageUrl} label="Learn more" testId="flyer-qr-page" />
            </div>
          </section>

          <footer className="px-[16mm] pb-[8mm] text-[10px] text-foreground/60 flex justify-between gap-4 border-t border-border pt-3 mx-[16mm] !px-0">
            <span>{CLINIC.name} · {CLINIC.address.line1}, {CLINIC.address.line2}</span>
            <span>Dr. Gurkiran Kaur · MS Ophthalmology</span>
          </footer>
        </article>
      </div>
    </div>
  );
}
